/** This file must only contain pure code and pure imports */

/* eslint-disable @typescript-eslint/naming-convention */
import { serialize, serializeAsColor3, expandToProperty, serializeAsTexture, serializeAsVector3 } from "../../Misc/decorators";
import { SmartArray } from "../../Misc/smartArray";
import { Logger } from "../../Misc/logger";
import { type Nullable, type int, type float } from "../../types";
import { type Scene } from "../../scene.pure";
import { type Matrix, TmpVectors, Vector3, Vector4 } from "../../Maths/math.vector.pure";
import { VertexBuffer } from "../../Buffers/buffer.pure";
import { type SubMesh } from "../../Meshes/subMesh.pure";
import { type AbstractMesh } from "../../Meshes/abstractMesh.pure";
import { type Mesh } from "../../Meshes/mesh.pure";
import { type IEffectCreationOptions } from "../../Materials/effect.pure";
import { MaterialDefines } from "../../Materials/materialDefines";
import { PushMaterial } from "../../Materials/pushMaterial";
import { ImageProcessingDefinesMixin } from "../../Materials/imageProcessingConfiguration.defines";
import { ImageProcessingConfiguration, RegisterImageProcessingConfiguration } from "../../Materials/imageProcessingConfiguration.pure";
import { type BaseTexture } from "../../Materials/Textures/baseTexture.pure";
import { type RenderTargetTexture } from "../../Materials/Textures/renderTargetTexture.pure";
import { type IShadowLight } from "../../Lights/shadowLight";
import { Constants } from "../../Engines/constants";
import { MaterialFlags } from "../materialFlags";
import { Color3 } from "../../Maths/math.color.pure";

import { EffectFallbacks } from "../effectFallbacks";
import { AddClipPlaneUniforms, BindClipPlane } from "../clipPlaneMaterialHelper";
import {
    BindBonesParameters,
    BindFogParameters,
    BindLights,
    BindLogDepth,
    BindTextureMatrix,
    BindIBLParameters,
    BindIBLSamplers,
    HandleFallbacksForShadows,
    PrepareAttributesForBones,
    PrepareAttributesForInstances,
    PrepareDefinesForAttributes,
    PrepareDefinesForFrameBoundValues,
    PrepareDefinesForLights,
    PrepareDefinesForIBL,
    PrepareDefinesForMergedUV,
    PrepareDefinesForMisc,
    PrepareDefinesForMultiview,
    PrepareUniformsAndSamplersList,
    PrepareUniformsAndSamplersForIBL,
    PrepareUniformLayoutForIBL,
    AreLightsTexturesReady,
} from "../materialHelper.functions";
import { SerializationHelper } from "../../Misc/decorators.serialization";
import { _ShaderImportLoader } from "../../Misc/shaderImportLoader";
import { ImageProcessingMixin } from "../imageProcessing";
import { RegisterClass } from "../../Misc/typeStore";

class BackgroundMaterialDefinesBase extends MaterialDefines {}

/**
 * Background material defines definition.
 * @internal Mainly internal Use
 */
class BackgroundMaterialDefines extends ImageProcessingDefinesMixin(BackgroundMaterialDefinesBase) {
    /**
     * True if the diffuse texture is in use.
     */
    public DIFFUSE = false;

    /**
     * The direct UV channel to use.
     */
    public DIFFUSEDIRECTUV = 0;

    /**
     * True if the diffuse texture is in gamma space.
     */
    public GAMMADIFFUSE = false;

    /**
     * True if the diffuse texture has opacity in the alpha channel.
     */
    public DIFFUSEHASALPHA = false;

    /**
     * True if you want the material to fade to transparent at grazing angle.
     */
    public OPACITYFRESNEL = false;

    /**
     * True if an extra blur needs to be added in the reflection.
     */
    public REFLECTIONBLUR = false;

    /**
     * True if you want the material to fade to reflection at grazing angle.
     */
    public REFLECTIONFRESNEL = false;

    /**
     * True if you want the material to falloff as far as you move away from the scene center.
     */
    public REFLECTIONFALLOFF = false;

    /**
     * False if the current Webgl implementation does not support the texture lod extension.
     */
    public TEXTURELODSUPPORT = false;

    /**
     * True to ensure the data are premultiplied.
     */
    public PREMULTIPLYALPHA = false;

    /**
     * True if the texture contains cooked RGB values and not gray scaled multipliers.
     */
    public USERGBCOLOR = false;

    /**
     * True if highlight and shadow levels have been specified. It can help ensuring the main perceived color
     * stays aligned with the desired configuration.
     */
    public USEHIGHLIGHTANDSHADOWCOLORS = false;

    /**
     * True if only shadows must be rendered
     */
    public BACKMAT_SHADOWONLY = false;

    /**
     * True to add noise in order to reduce the banding effect.
     */
    public NOISE = false;

    /**
     * is the reflection texture in BGR color scheme?
     * Mainly used to solve a bug in ios10 video tag
     */
    public REFLECTIONBGR = false;

    /**
     * True if ground projection has been enabled.
     */
    public PROJECTED_GROUND = false;

    /** Defines whether reflection is enabled */
    public REFLECTION = false;
    /** Defines whether the reflection map is 3D */
    public REFLECTIONMAP_3D = false;
    /** Defines whether the reflection map is spherical */
    public REFLECTIONMAP_SPHERICAL = false;
    /** Defines whether the reflection map is planar */
    public REFLECTIONMAP_PLANAR = false;
    /** Defines whether the reflection map is cubic */
    public REFLECTIONMAP_CUBIC = false;
    /** Defines whether the reflection map is in projection mode */
    public REFLECTIONMAP_PROJECTION = false;
    /** Defines whether the reflection map is a skybox */
    public REFLECTIONMAP_SKYBOX = false;
    /** Defines whether the reflection map is explicit */
    public REFLECTIONMAP_EXPLICIT = false;
    /** Defines whether the reflection map is equirectangular */
    public REFLECTIONMAP_EQUIRECTANGULAR = false;
    /** Defines whether the reflection map is fixed equirectangular */
    public REFLECTIONMAP_EQUIRECTANGULAR_FIXED = false;
    /** Defines whether the reflection map is mirrored fixed equirectangular */
    public REFLECTIONMAP_MIRROREDEQUIRECTANGULAR_FIXED = false;
    /** Defines whether the cubic map should be inverted */
    public INVERTCUBICMAP = false;
    /** Defines whether the reflection map uses opposite Z */
    public REFLECTIONMAP_OPPOSITEZ = false;
    /** Defines whether LOD is stored in the reflection alpha channel */
    public LODINREFLECTIONALPHA = false;
    /** Defines whether the reflection uses gamma space */
    public GAMMAREFLECTION = false;
    /** Defines whether the reflection is in RGBD format */
    public RGBDREFLECTION = false;
    /** Defines whether the equirectangular reflection uses a custom field of view */
    public EQUIRECTANGULAR_RELFECTION_FOV = false;

    /** Defines whether main UV1 channel is used */
    public MAINUV1 = false;
    /** Defines whether main UV2 channel is used */
    public MAINUV2 = false;
    /** Defines whether UV1 is used */
    public UV1 = false;
    /** Defines whether UV2 is used */
    public UV2 = false;
    /** Defines whether clip plane 1 is enabled */
    public CLIPPLANE = false;
    /** Defines whether clip plane 2 is enabled */
    public CLIPPLANE2 = false;
    /** Defines whether clip plane 3 is enabled */
    public CLIPPLANE3 = false;
    /** Defines whether clip plane 4 is enabled */
    public CLIPPLANE4 = false;
    /** Defines whether clip plane 5 is enabled */
    public CLIPPLANE5 = false;
    /** Defines whether clip plane 6 is enabled */
    public CLIPPLANE6 = false;
    /** Defines whether point size is used */
    public POINTSIZE = false;
    /** Defines whether fog is enabled */
    public FOG = false;
    /** Defines whether normals are available */
    public NORMAL = false;
    /** Defines the number of bone influencers */
    public NUM_BONE_INFLUENCERS = 0;
    /** Defines the number of bones per mesh */
    public BonesPerMesh = 0;
    /** Defines whether instances are used */
    public INSTANCES = false;
    /** Defines whether shadow uses float textures */
    public SHADOWFLOAT = false;
    /** Defines whether logarithmic depth is enabled */
    public LOGARITHMICDEPTH = false;
    /** Defines whether non-uniform scaling is applied */
    public NONUNIFORMSCALING = false;
    /** Defines whether alpha testing is enabled */
    public ALPHATEST = false;

    /**
     * Constructor of the defines.
     */
    constructor() {
        super();
        this.rebuild();
    }
}

class BackgroundMaterialBase extends ImageProcessingMixin(PushMaterial) {}
/**
 * Background material used to create an efficient environment around your scene.
 * #157MGZ: simple test
 */
export class BackgroundMaterial extends BackgroundMaterialBase {
    /**
     * Standard reflectance value at parallel view angle.
     */
    public static StandardReflectance0 = 0.05;

    /**
     * Standard reflectance value at grazing angle.
     */
    public static StandardReflectance90 = 0.5;

    @serializeAsColor3()
    protected _primaryColor: Color3;
    /**
     * Key light Color (multiply against the environment texture)
     */
    @expandToProperty("_markAllSubMeshesAsLightsDirty")
    public accessor primaryColor = Color3.White();

    @serializeAsColor3()
    protected __perceptualColor: Nullable<Color3>;
    /**
     * Experimental Internal Use Only.
     *
     * Key light Color in "perceptual value" meaning the color you would like to see on screen.
     * This acts as a helper to set the primary color to a more "human friendly" value.
     * Conversion to linear space as well as exposure and tone mapping correction will be applied to keep the
     * output color as close as possible from the chosen value.
     * (This does not account for contrast color grading and color curves as they are considered post effect and not directly
     * part of lighting setup.)
     */
    public get _perceptualColor(): Nullable<Color3> {
        return this.__perceptualColor;
    }
    public set _perceptualColor(value: Nullable<Color3>) {
        this.__perceptualColor = value;
        this._computePrimaryColorFromPerceptualColor();
        this._markAllSubMeshesAsLightsDirty();
    }

    @serialize()
    protected _primaryColorShadowLevel: float = 0;
    /**
     * Defines the level of the shadows (dark area of the reflection map) in order to help scaling the colors.
     * The color opposite to the primary color is used at the level chosen to define what the black area would look.
     */
    public get primaryColorShadowLevel(): float {
        return this._primaryColorShadowLevel;
    }
    public set primaryColorShadowLevel(value: float) {
        this._primaryColorShadowLevel = value;
        this._computePrimaryColors();
        this._markAllSubMeshesAsLightsDirty();
    }

    @serialize()
    protected _primaryColorHighlightLevel: float = 0;
    /**
     * Defines the level of the highlights (highlight area of the reflection map) in order to help scaling the colors.
     * The primary color is used at the level chosen to define what the white area would look.
     */
    public get primaryColorHighlightLevel(): float {
        return this._primaryColorHighlightLevel;
    }
    public set primaryColorHighlightLevel(value: float) {
        this._primaryColorHighlightLevel = value;
        this._computePrimaryColors();
        this._markAllSubMeshesAsLightsDirty();
    }

    @serializeAsTexture()
    protected _reflectionTexture: Nullable<BaseTexture>;
    /**
     * Reflection Texture used in the material.
     * Should be author in a specific way for the best result (refer to the documentation).
     */
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor reflectionTexture: Nullable<BaseTexture> = null;

    @serialize()
    protected _reflectionBlur: float;
    /**
     * Reflection Texture level of blur.
     *
     * Can be use to reuse an existing HDR Texture and target a specific LOD to prevent authoring the
     * texture twice.
     */
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor reflectionBlur: float = 0;

    @serializeAsTexture()
    protected _diffuseTexture: Nullable<BaseTexture>;
    /**
     * Diffuse Texture used in the material.
     * Should be author in a specific way for the best result (refer to the documentation).
     */
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor diffuseTexture: Nullable<BaseTexture> = null;

    protected _shadowLights: Nullable<IShadowLight[]> = null;
    /**
     * Specify the list of lights casting shadow on the material.
     * All scene shadow lights will be included if null.
     */
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor shadowLights: Nullable<IShadowLight[]> = null;

    @serialize()
    protected _shadowLevel: float;
    /**
     * Helps adjusting the shadow to a softer level if required.
     * 0 means black shadows and 1 means no shadows.
     */
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor shadowLevel: float = 0;

    @serializeAsVector3()
    protected _sceneCenter: Vector3;
    /**
     * In case of opacity Fresnel or reflection falloff, this is use as a scene center.
     * It is usually zero but might be interesting to modify according to your setup.
     */
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor sceneCenter: Vector3 = Vector3.Zero();

    @serialize()
    protected _opacityFresnel: boolean;
    /**
     * This helps specifying that the material is falling off to the sky box at grazing angle.
     * This helps ensuring a nice transition when the camera goes under the ground.
     */
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor opacityFresnel: boolean = true;

    @serialize()
    protected _reflectionFresnel: boolean;
    /**
     * This helps specifying that the material is falling off from diffuse to the reflection texture at grazing angle.
     * This helps adding a mirror texture on the ground.
     */
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor reflectionFresnel: boolean = false;

    @serialize()
    protected _reflectionFalloffDistance: number;
    /**
     * This helps specifying the falloff radius off the reflection texture from the sceneCenter.
     * This helps adding a nice falloff effect to the reflection if used as a mirror for instance.
     */
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor reflectionFalloffDistance: number = 0.0;

    @serialize()
    protected _reflectionAmount: number;
    /**
     * This specifies the weight of the reflection against the background in case of reflection Fresnel.
     */
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor reflectionAmount: number = 1.0;

    @serialize()
    protected _reflectionReflectance0: number;
    /**
     * This specifies the weight of the reflection at grazing angle.
     */
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor reflectionReflectance0: number = 0.05;

    @serialize()
    protected _reflectionReflectance90: number;
    /**
     * This specifies the weight of the reflection at a perpendicular point of view.
     */
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor reflectionReflectance90: number = 0.5;

    /**
     * Sets the reflection reflectance fresnel values according to the default standard
     * empirically know to work well :-)
     */
    public set reflectionStandardFresnelWeight(value: number) {
        let reflectionWeight = value;

        if (reflectionWeight < 0.5) {
            reflectionWeight = reflectionWeight * 2.0;
            this.reflectionReflectance0 = BackgroundMaterial.StandardReflectance0 * reflectionWeight;
            this.reflectionReflectance90 = BackgroundMaterial.StandardReflectance90 * reflectionWeight;
        } else {
            reflectionWeight = reflectionWeight * 2.0 - 1.0;
            this.reflectionReflectance0 = BackgroundMaterial.StandardReflectance0 + (1.0 - BackgroundMaterial.StandardReflectance0) * reflectionWeight;
            this.reflectionReflectance90 = BackgroundMaterial.StandardReflectance90 + (1.0 - BackgroundMaterial.StandardReflectance90) * reflectionWeight;
        }
    }

    @serialize()
    protected _useRGBColor: boolean;
    /**
     * Helps to directly use the maps channels instead of their level.
     */
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor useRGBColor: boolean = true;

    @serialize()
    protected _enableNoise: boolean;
    /**
     * This helps reducing the banding effect that could occur on the background.
     */
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor enableNoise: boolean = false;

    /**
     * The current fov(field of view) multiplier, 0.0 - 2.0. Defaults to 1.0. Lower values "zoom in" and higher values "zoom out".
     * Best used when trying to implement visual zoom effects like fish-eye or binoculars while not adjusting camera fov.
     * Recommended to be keep at 1.0 except for special cases.
     */
    public get fovMultiplier(): number {
        return this._fovMultiplier;
    }
    public set fovMultiplier(value: number) {
        if (isNaN(value)) {
            value = 1.0;
        }
        this._fovMultiplier = Math.max(0.0, Math.min(2.0, value));
    }
    private _fovMultiplier: float = 1.0;

    /**
     * Enable the FOV adjustment feature controlled by fovMultiplier.
     */
    public useEquirectangularFOV: boolean = false;

    @serialize()
    private _maxSimultaneousLights: int = 4;
    /**
     * Number of Simultaneous lights allowed on the material.
     */
    @expandToProperty("_markAllSubMeshesAsTexturesDirty")
    public accessor maxSimultaneousLights: int = 4;

    @serialize()
    private _shadowOnly: boolean = false;
    /**
     * Make the material only render shadows
     */
    @expandToProperty("_markAllSubMeshesAsLightsDirty")
    public accessor shadowOnly: boolean = false;

    /**
     * Due to a bug in iOS10, video tags (which are using the background material) are in BGR and not RGB.
     * Setting this flag to true (not done automatically!) will convert it back to RGB.
     */
    public switchToBGR: boolean = false;

    private _enableGroundProjection: boolean = false;
    /**
     * Enables the ground projection mode on the material.
     * @see https://doc.babylonjs.com/features/featuresDeepDive/environment/skybox#ground-projection
     */
    @serialize()
    @expandToProperty("_markAllSubMeshesAsMiscDirty")
    public accessor enableGroundProjection: boolean = false;

    /**
     * Defines the radius of the projected ground if enableGroundProjection is true.
     * @see https://doc.babylonjs.com/features/featuresDeepDive/environment/skybox#ground-projection
     */
    @serialize()
    public projectedGroundRadius = 1000;

    /**
     * Defines the height of the projected ground if enableGroundProjection is true.
     * @see https://doc.babylonjs.com/features/featuresDeepDive/environment/skybox#ground-projection
     */
    @serialize()
    public projectedGroundHeight = 10;

    // Temp values kept as cache in the material.
    private _renderTargets = new SmartArray<RenderTargetTexture>(16);
    private _reflectionControls = Vector4.Zero();
    private _white = Color3.White();
    private _primaryShadowColor = Color3.Black();
    private _primaryHighlightColor = Color3.Black();

    private static readonly _ShaderLoader = /*#__PURE__*/ new _ShaderImportLoader(
        () => [import("../../Shaders/background.vertex"), import("../../Shaders/background.fragment")],
        () => [import("../../ShadersWGSL/background.vertex"), import("../../ShadersWGSL/background.fragment")]
    );

    /**
     * Instantiates a Background Material in the given scene
     * @param name The friendly name of the material
     * @param scene The scene to add the material to
     * @param forceGLSL Use the GLSL code generation for the shader (even on WebGPU). Default is false
     */
    constructor(name: string, scene?: Scene, forceGLSL = false) {
        super(name, scene, undefined, forceGLSL);

        // Setup the default processing configuration to the scene.
        this._attachImageProcessingConfiguration(null);

        this.getRenderTargetTextures = (): SmartArray<RenderTargetTexture> => {
            this._renderTargets.reset();

            if (this._diffuseTexture && this._diffuseTexture.isRenderTarget) {
                this._renderTargets.push(this._diffuseTexture as RenderTargetTexture);
            }

            if (this._reflectionTexture && this._reflectionTexture.isRenderTarget) {
                this._renderTargets.push(this._reflectionTexture as RenderTargetTexture);
            }

            return this._renderTargets;
        };
    }

    /**
     * Gets a boolean indicating that current material needs to register RTT
     */
    public override get hasRenderTargetTextures(): boolean {
        if (this._diffuseTexture && this._diffuseTexture.isRenderTarget) {
            return true;
        }

        if (this._reflectionTexture && this._reflectionTexture.isRenderTarget) {
            return true;
        }

        return false;
    }

    /**
     * The entire material has been created in order to prevent overdraw.
     * @returns false
     */
    public override needAlphaTesting(): boolean {
        return true;
    }

    /**
     * The entire material has been created in order to prevent overdraw.
     * @returns true if blending is enable
     */
    public override needAlphaBlending(): boolean {
        return this.alpha < 1 || (this._diffuseTexture != null && this._diffuseTexture.hasAlpha) || this._shadowOnly;
    }

    /**
     * Checks whether the material is ready to be rendered for a given mesh.
     * @param mesh The mesh to render
     * @param subMesh The submesh to check against
     * @param useInstances Specify wether or not the material is used with instances
     * @returns true if all the dependencies are ready (Textures, Effects...)
     */
    public override isReadyForSubMesh(mesh: AbstractMesh, subMesh: SubMesh, useInstances: boolean = false): boolean {
        const drawWrapper = subMesh._drawWrapper;

        if (drawWrapper.effect && this.isFrozen) {
            if (drawWrapper._wasPreviouslyReady && drawWrapper._wasPreviouslyUsingInstances === useInstances) {
                return true;
            }
        }

        if (!subMesh.materialDefines) {
            subMesh.materialDefines = new BackgroundMaterialDefines();
        }

        const scene = this.getScene();
        const defines = <BackgroundMaterialDefines>subMesh.materialDefines;

        if (this._isReadyForSubMesh(subMesh)) {
            return true;
        }

        const engine = scene.getEngine();

        // Lights
        PrepareDefinesForLights(scene, mesh, defines, false, this._maxSimultaneousLights);
        defines._needNormals = true;

        if (!AreLightsTexturesReady(scene, mesh, this._maxSimultaneousLights)) {
            return false;
        }

        // Multiview
        PrepareDefinesForMultiview(scene, defines);

        // Textures
        if (defines._areTexturesDirty) {
            defines._needUVs = false;
            if (scene.texturesEnabled) {
                if (scene.getEngine().getCaps().textureLOD) {
                    defines.TEXTURELODSUPPORT = true;
                }

                if (this._diffuseTexture && MaterialFlags.DiffuseTextureEnabled) {
                    if (!this._diffuseTexture.isReadyOrNotBlocking()) {
                        return false;
                    }

                    PrepareDefinesForMergedUV(this._diffuseTexture, defines, "DIFFUSE");
                    defines.DIFFUSEHASALPHA = this._diffuseTexture.hasAlpha;
                    defines.GAMMADIFFUSE = this._diffuseTexture.gammaSpace;
                    defines.OPACITYFRESNEL = this._opacityFresnel;
                } else {
                    defines.DIFFUSE = false;
                    defines.DIFFUSEDIRECTUV = 0;
                    defines.DIFFUSEHASALPHA = false;
                    defines.GAMMADIFFUSE = false;
                    defines.OPACITYFRESNEL = false;
                }

                const reflectionTexture = this._reflectionTexture;
                PrepareDefinesForIBL(scene, reflectionTexture, defines);
                if (reflectionTexture && MaterialFlags.ReflectionTextureEnabled) {
                    if (!reflectionTexture.isReadyOrNotBlocking()) {
                        return false;
                    }
                    defines.EQUIRECTANGULAR_RELFECTION_FOV = this.useEquirectangularFOV;
                    defines.REFLECTIONBGR = this.switchToBGR;
                    defines.REFLECTIONBLUR = this._reflectionBlur > 0;

                    if (this.reflectionFresnel) {
                        defines.REFLECTIONFRESNEL = true;
                        defines.REFLECTIONFALLOFF = this.reflectionFalloffDistance > 0;

                        this._reflectionControls.x = this.reflectionAmount;
                        this._reflectionControls.y = this.reflectionReflectance0;
                        this._reflectionControls.z = this.reflectionReflectance90;
                        this._reflectionControls.w = 1 / this.reflectionFalloffDistance;
                    } else {
                        defines.REFLECTIONFRESNEL = false;
                        defines.REFLECTIONFALLOFF = false;
                    }
                } else {
                    defines.REFLECTIONFRESNEL = false;
                    defines.REFLECTIONFALLOFF = false;
                    defines.REFLECTIONBLUR = false;
                }
            }

            defines.PREMULTIPLYALPHA = this.alphaMode === Constants.ALPHA_PREMULTIPLIED || this.alphaMode === Constants.ALPHA_PREMULTIPLIED_PORTERDUFF;
            defines.USERGBCOLOR = this._useRGBColor;
            defines.NOISE = this._enableNoise;
        }

        if (defines._areLightsDirty) {
            defines.USEHIGHLIGHTANDSHADOWCOLORS = !this._useRGBColor && (this._primaryColorShadowLevel !== 0 || this._primaryColorHighlightLevel !== 0);
            defines.BACKMAT_SHADOWONLY = this._shadowOnly;
        }

        if (defines._areImageProcessingDirty && this._imageProcessingConfiguration) {
            if (!this._imageProcessingConfiguration.isReady()) {
                return false;
            }

            this._imageProcessingConfiguration.prepareDefines(defines);
        }

        if (defines._areMiscDirty) {
            if (defines.REFLECTIONMAP_3D && this._enableGroundProjection) {
                defines.PROJECTED_GROUND = true;
                defines.REFLECTIONMAP_SKYBOX = true;
            } else {
                defines.PROJECTED_GROUND = false;
            }
        }

        // Misc.
        PrepareDefinesForMisc(
            mesh,
            scene,
            this._useLogarithmicDepth,
            this.pointsCloud,
            this.fogEnabled,
            this.needAlphaTestingForMesh(mesh),
            defines,
            undefined,
            undefined,
            undefined,
            this._isVertexOutputInvariant
        );

        // Values that need to be evaluated on every frame
        PrepareDefinesForFrameBoundValues(scene, engine, this, defines, useInstances, null, subMesh.getRenderingMesh().hasThinInstances);

        // Attribs
        if (PrepareDefinesForAttributes(mesh, defines, false, true, false)) {
            if (mesh) {
                if (!scene.getEngine().getCaps().standardDerivatives && !mesh.isVerticesDataPresent(VertexBuffer.NormalKind)) {
                    mesh.createNormals(true);
                    Logger.Warn("BackgroundMaterial: Normals have been created for the mesh: " + mesh.name);
                }
            }
        }

        // Get correct effect
        if (defines.isDirty) {
            defines.markAsProcessed();
            scene.resetCachedMaterial();

            // Fallbacks
            const fallbacks = new EffectFallbacks();
            if (defines.FOG) {
                fallbacks.addFallback(0, "FOG");
            }

            if (defines.POINTSIZE) {
                fallbacks.addFallback(1, "POINTSIZE");
            }

            if (defines.MULTIVIEW) {
                fallbacks.addFallback(0, "MULTIVIEW");
            }

            HandleFallbacksForShadows(defines, fallbacks, this._maxSimultaneousLights);

            //Attributes
            const attribs = [VertexBuffer.PositionKind];

            if (defines.NORMAL) {
                attribs.push(VertexBuffer.NormalKind);
            }

            if (defines.UV1) {
                attribs.push(VertexBuffer.UVKind);
            }

            if (defines.UV2) {
                attribs.push(VertexBuffer.UV2Kind);
            }

            PrepareAttributesForBones(attribs, mesh, defines, fallbacks);
            PrepareAttributesForInstances(attribs, defines);

            const uniforms = [
                "world",
                "view",
                "viewProjection",
                "vEyePosition",
                "vLightsType",
                "vFogInfos",
                "vFogColor",
                "pointSize",
                "mBones",

                "vPrimaryColor",
                "vPrimaryColorShadow",
                "fFovMultiplier",

                "shadowLevel",
                "alpha",

                "vBackgroundCenter",
                "vReflectionControl",

                "vDiffuseInfos",
                "diffuseMatrix",

                "projectedGroundInfos",
                "logarithmicDepthConstant",
            ];

            AddClipPlaneUniforms(uniforms);
            const samplers = ["diffuseSampler"];
            PrepareUniformsAndSamplersForIBL(uniforms, samplers, false);
            const uniformBuffers = ["Material", "Scene"];

            if (ImageProcessingConfiguration) {
                ImageProcessingConfiguration.PrepareUniforms(uniforms, defines);
                ImageProcessingConfiguration.PrepareSamplers(samplers, defines);
            }

            PrepareUniformsAndSamplersList(<IEffectCreationOptions>{
                uniformsNames: uniforms,
                uniformBuffersNames: uniformBuffers,
                samplers: samplers,
                defines: defines,
                maxSimultaneousLights: this._maxSimultaneousLights,
                shaderLanguage: this._shaderLanguage,
            });

            const join = defines.toString();
            const effect = scene.getEngine().createEffect(
                "background",
                <IEffectCreationOptions>{
                    attributes: attribs,
                    uniformsNames: uniforms,
                    uniformBuffersNames: uniformBuffers,
                    samplers: samplers,
                    defines: join,
                    fallbacks: fallbacks,
                    onCompiled: this.onCompiled,
                    onError: this.onError,
                    indexParameters: { maxSimultaneousLights: this._maxSimultaneousLights },
                    shaderLanguage: this._shaderLanguage,
                    extraInitializationsAsync: BackgroundMaterial._ShaderLoader.getLoadCallback(this._shaderLanguage),
                },
                engine
            );
            subMesh.setEffect(effect, defines, this._materialContext);

            this.buildUniformLayout();
        }

        if (!subMesh.effect || !subMesh.effect.isReady()) {
            return false;
        }

        defines._renderId = scene.getRenderId();
        drawWrapper._wasPreviouslyReady = true;
        drawWrapper._wasPreviouslyUsingInstances = useInstances;

        this._checkScenePerformancePriority();

        return true;
    }

    /**
     * Compute the primary color according to the chosen perceptual color.
     */
    private _computePrimaryColorFromPerceptualColor(): void {
        if (!this.__perceptualColor) {
            return;
        }

        this._primaryColor.copyFrom(this.__perceptualColor);

        // Revert gamma space.
        this._primaryColor.toLinearSpaceToRef(this._primaryColor, this.getScene().getEngine().useExactSrgbConversions);

        // Revert image processing configuration.
        if (this._imageProcessingConfiguration) {
            // Revert Exposure.
            this._primaryColor.scaleToRef(1 / this._imageProcessingConfiguration.exposure, this._primaryColor);
        }

        this._computePrimaryColors();
    }

    /**
     * Compute the highlights and shadow colors according to their chosen levels.
     */
    private _computePrimaryColors(): void {
        if (this._primaryColorShadowLevel === 0 && this._primaryColorHighlightLevel === 0) {
            return;
        }

        // Find the highlight color based on the configuration.
        this._primaryColor.scaleToRef(this._primaryColorShadowLevel, this._primaryShadowColor);
        this._primaryColor.subtractToRef(this._primaryShadowColor, this._primaryShadowColor);

        // Find the shadow color based on the configuration.
        this._white.subtractToRef(this._primaryColor, this._primaryHighlightColor);
        this._primaryHighlightColor.scaleToRef(this._primaryColorHighlightLevel, this._primaryHighlightColor);
        this._primaryColor.addToRef(this._primaryHighlightColor, this._primaryHighlightColor);
    }

    /**
     * Build the uniform buffer used in the material.
     */
    public override buildUniformLayout(): void {
        // Order is important !
        this._uniformBuffer.addUniform("vPrimaryColor", 4);
        this._uniformBuffer.addUniform("vPrimaryColorShadow", 4);
        this._uniformBuffer.addUniform("vDiffuseInfos", 2);
        this._uniformBuffer.addUniform("diffuseMatrix", 16);
        this._uniformBuffer.addUniform("fFovMultiplier", 1);
        this._uniformBuffer.addUniform("pointSize", 1);
        this._uniformBuffer.addUniform("shadowLevel", 1);
        this._uniformBuffer.addUniform("alpha", 1);
        this._uniformBuffer.addUniform("vBackgroundCenter", 3);
        this._uniformBuffer.addUniform("vReflectionControl", 4);
        this._uniformBuffer.addUniform("projectedGroundInfos", 2);

        PrepareUniformLayoutForIBL(this._uniformBuffer, true, false, false);

        this._uniformBuffer.create();
    }

    /**
     * Unbind the material.
     */
    public override unbind(): void {
        if (this._diffuseTexture && this._diffuseTexture.isRenderTarget) {
            this._uniformBuffer.setTexture("diffuseSampler", null);
        }

        if (this._reflectionTexture && this._reflectionTexture.isRenderTarget) {
            this._uniformBuffer.setTexture("reflectionSampler", null);
        }

        super.unbind();
    }

    /**
     * Bind only the world matrix to the material.
     * @param world The world matrix to bind.
     */
    public override bindOnlyWorldMatrix(world: Matrix): void {
        this._activeEffect!.setMatrix("world", world);
    }

    /**
     * Bind the material for a dedicated submesh (every used meshes will be considered opaque).
     * @param world The world matrix to bind.
     * @param mesh the mesh to bind for.
     * @param subMesh The submesh to bind for.
     */
    public override bindForSubMesh(world: Matrix, mesh: Mesh, subMesh: SubMesh): void {
        const scene = this.getScene();

        const defines = <BackgroundMaterialDefines>subMesh.materialDefines;
        if (!defines) {
            return;
        }

        const effect = subMesh.effect;
        if (!effect) {
            return;
        }
        this._activeEffect = effect;

        // Matrices
        this.bindOnlyWorldMatrix(world);

        // Bones
        BindBonesParameters(mesh, this._activeEffect);

        const mustRebind = this._mustRebind(scene, effect, subMesh, mesh.visibility);
        const needToAlwaysBindUniformBuffers = scene.getEngine()._features.needToAlwaysBindUniformBuffers;
        if (mustRebind) {
            this._uniformBuffer.bindToEffect(effect, "Material");

            this.bindViewProjection(effect);

            const reflectionTexture = this._reflectionTexture;
            if (!this._uniformBuffer.useUbo || !this.isFrozen || !this._uniformBuffer.isSync || subMesh._drawWrapper._forceRebindOnNextCall) {
                // Texture uniforms
                if (scene.texturesEnabled) {
                    if (this._diffuseTexture && MaterialFlags.DiffuseTextureEnabled) {
                        this._uniformBuffer.updateFloat2("vDiffuseInfos", this._diffuseTexture.coordinatesIndex, this._diffuseTexture.level);
                        BindTextureMatrix(this._diffuseTexture, this._uniformBuffer, "diffuse");
                    }

                    BindIBLParameters(scene, defines, this._uniformBuffer, Color3.White(), reflectionTexture, false, true, false, false, false, false, this._reflectionBlur);
                }

                if (this.shadowLevel > 0) {
                    this._uniformBuffer.updateFloat("shadowLevel", this.shadowLevel);
                }
                this._uniformBuffer.updateFloat("alpha", this.alpha);

                // Point size
                if (this.pointsCloud) {
                    this._uniformBuffer.updateFloat("pointSize", this.pointSize);
                }

                if (defines.USEHIGHLIGHTANDSHADOWCOLORS) {
                    this._uniformBuffer.updateColor4("vPrimaryColor", this._primaryHighlightColor, 1.0);
                    this._uniformBuffer.updateColor4("vPrimaryColorShadow", this._primaryShadowColor, 1.0);
                } else {
                    this._uniformBuffer.updateColor4("vPrimaryColor", this._primaryColor, 1.0);
                }
            }

            this._uniformBuffer.updateFloat("fFovMultiplier", this._fovMultiplier);

            // Fresnel
            if (defines.REFLECTIONFRESNEL) {
                this._uniformBuffer.updateFloat4(
                    "vReflectionControl",
                    this._reflectionControls.x,
                    this._reflectionControls.y,
                    this._reflectionControls.z,
                    this._reflectionControls.w
                );
            }
            if ((defines.REFLECTIONFRESNEL && defines.REFLECTIONFALLOFF) || defines.OPACITYFRESNEL) {
                const center = TmpVectors.Vector3[0].copyFrom(this.sceneCenter).subtractInPlace(scene.floatingOriginOffset);
                this._uniformBuffer.updateFloat3("vBackgroundCenter", center.x, center.y, center.z);
            }

            // Textures
            if (scene.texturesEnabled) {
                if (this._diffuseTexture && MaterialFlags.DiffuseTextureEnabled) {
                    this._uniformBuffer.setTexture("diffuseSampler", this._diffuseTexture);
                }

                if (reflectionTexture && MaterialFlags.ReflectionTextureEnabled) {
                    BindIBLSamplers(scene, defines, this._uniformBuffer, reflectionTexture);
                }

                if (defines.PROJECTED_GROUND) {
                    this._uniformBuffer.updateFloat2("projectedGroundInfos", this.projectedGroundRadius, this.projectedGroundHeight);
                }
            }

            // Clip plane
            BindClipPlane(this._activeEffect, this, scene);

            scene.bindEyePosition(effect);
        } else if (needToAlwaysBindUniformBuffers) {
            this._uniformBuffer.bindToEffect(effect, "Material");
            this._needToBindSceneUbo = true;
        }

        if ((mustRebind || !this.isFrozen || needToAlwaysBindUniformBuffers) && scene.lightsEnabled) {
            BindLights(scene, mesh, this._activeEffect, defines, this._maxSimultaneousLights);
        }

        if (mustRebind || !this.isFrozen) {
            // View
            this.bindView(effect);

            // Fog
            BindFogParameters(scene, mesh, this._activeEffect, true);

            // Log. depth
            if (this._useLogarithmicDepth) {
                BindLogDepth(defines, effect, scene);
            }

            // image processing
            if (this._imageProcessingConfiguration) {
                this._imageProcessingConfiguration.bind(this._activeEffect);
            }
        }

        this._afterBind(mesh, this._activeEffect, subMesh);

        this._uniformBuffer.update();
    }

    /**
     * Checks to see if a texture is used in the material.
     * @param texture - Base texture to use.
     * @returns - Boolean specifying if a texture is used in the material.
     */
    public override hasTexture(texture: BaseTexture): boolean {
        if (super.hasTexture(texture)) {
            return true;
        }

        if (this._reflectionTexture === texture) {
            return true;
        }

        if (this._diffuseTexture === texture) {
            return true;
        }

        return false;
    }

    /**
     * Dispose the material.
     * @param forceDisposeEffect Force disposal of the associated effect.
     * @param forceDisposeTextures Force disposal of the associated textures.
     */
    public override dispose(forceDisposeEffect: boolean = false, forceDisposeTextures: boolean = false): void {
        if (forceDisposeTextures) {
            if (this.diffuseTexture) {
                this.diffuseTexture.dispose();
            }
            if (this.reflectionTexture) {
                this.reflectionTexture.dispose();
            }
        }

        this._renderTargets.dispose();

        if (this._imageProcessingConfiguration && this._imageProcessingObserver) {
            this._imageProcessingConfiguration.onUpdateParameters.remove(this._imageProcessingObserver);
        }

        super.dispose(forceDisposeEffect);
    }

    /**
     * Clones the material.
     * @param name The cloned name.
     * @returns The cloned material.
     */
    public override clone(name: string): BackgroundMaterial {
        return SerializationHelper.Clone(() => new BackgroundMaterial(name, this.getScene()), this);
    }

    /**
     * Serializes the current material to its JSON representation.
     * @returns The JSON representation.
     */
    public override serialize(): any {
        const serializationObject = super.serialize();
        serializationObject.customType = "BABYLON.BackgroundMaterial";
        return serializationObject;
    }

    /**
     * Gets the class name of the material
     * @returns "BackgroundMaterial"
     */
    public override getClassName(): string {
        return "BackgroundMaterial";
    }

    /**
     * Parse a JSON input to create back a background material.
     * @param source The JSON data to parse
     * @param scene The scene to create the parsed material in
     * @param rootUrl The root url of the assets the material depends upon
     * @returns the instantiated BackgroundMaterial.
     */
    public static override Parse(source: any, scene: Scene, rootUrl: string): BackgroundMaterial {
        return SerializationHelper.Parse(() => new BackgroundMaterial(source.name, scene), source, scene, rootUrl);
    }
}

let _Registered = false;
/**
 * Register side effects for backgroundMaterial.
 * Safe to call multiple times; only the first call has an effect.
 */
export function RegisterBackgroundMaterial(): void {
    if (_Registered) {
        return;
    }
    _Registered = true;

    // BackgroundMaterial serializes its image processing configuration, so the parser must be registered for clone/parse to work.
    RegisterImageProcessingConfiguration();

    RegisterClass("BABYLON.BackgroundMaterial", BackgroundMaterial);
}
